import { Info } from '@mui/icons-material';
import { Alert, Button, Card, CardHeader, Divider, Tab, Tabs } from '@mui/material';
import Dialog from 'components/Dialog';
import FileDropZone from 'components/FileDropZone';
import ImagePreview from 'components/ImagePreview';
import { useCustomSnackbar } from 'components/NotificationSnackbars';
import TabPanel from 'components/TabPanel';
import { bytesToMegaBytesSize } from 'components/forms/utils';
import { DATE_FORMAT, FILE_EXTENSION, IMAGE_SIZE } from 'const';
import { File } from '@kinetic-ui/data-layer';
import { restClient } from 'kineticUtils/api';
import isEmpty from 'lodash/isEmpty';
import { VariantType } from 'notistack';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ImageCardContent from './ImageCardContent';
import ImageLibrarySelect from './ImageLibrarySelect';
import ImageList from './ImageList';
import useImageUploadStyles from './styles';
import { ImageUploadDialogProps } from './types';

const ImageUploadDialog: React.FunctionComponent<ImageUploadDialogProps> = (props) => {
  const {
    dialogOpen,
    setDialogOpen,
    moduleName,
    name,
    setFileCount,
    setValue,
    value,
    multiple,
    mode,
    sourceId,
  } = props;
  const classes = useImageUploadStyles();
  const [fileList, setFileList] = useState(value || []);
  const [previewImage, setPreviewImage] = useState<File | null>();
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(Boolean);
  const { enqueueCustomSnackbar } = useCustomSnackbar();
  const [file, setFile] = useState<File>();
  const [tabIndex, setTabIndex] = useState(0);
  const { t } = useTranslation('common');

  useEffect(() => {
    if (mode === 'library') {
      setTabIndex(1);
    }
  }, [mode]);

  //Discards the attached files
  const cancelUploadAction = () => {
    setPreviewImage(null);
    setDialogOpen(false);
    setFileList(value || []);
  };

  //Delete attached files from the UI
  const deleteImage = useCallback(() => {
    const uploadingFiles = fileList.filter((item) =>
      typeof item !== 'string' ? item?.id !== file?.id : item !== file
    );
    typeof previewImage !== 'string'
      ? previewImage && file?.id === previewImage['id'] && setPreviewImage(null)
      : previewImage && file === previewImage && setPreviewImage(null);
    setFileList(uploadingFiles);
    setDeleteDialogOpen(false);
  }, [file, fileList, previewImage]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const deleteConfirmation = (file: File, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteDialogOpen(true);
    setFile(file);
  };

  //Saves the attached files to saveIntegration
  const saveImages = () => {
    setValue(fileList);
    fileCount(fileList);
    setDialogOpen(false);
  };
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };
  const renderUploadActionButtons = () => {
    return (
      <>
        <Button size='small' onClick={cancelUploadAction} className={classes.btnText}>
          {t('cancel')}
        </Button>
        <Button
          size='small'
          variant='contained'
          color='secondary'
          onClick={saveImages}
          disabled={loading}
          className={classes.btnText}
        >
          {t('done')}
        </Button>
      </>
    );
  };

  const fileCount = useCallback(
    (imageDetails) => {
      if (imageDetails && imageDetails.length > 0) {
        const file = imageDetails.length === 1 ? t('file') : t('files');
        setFileCount(`${imageDetails.length as unknown as string} ${file} `);
      } else {
        setFileCount(t('noFileChosen'));
      }
    },
    [setFileCount, t]
  );

  //uploads the attached files to server
  const uploadSiteImages = async (files: File[]) => {
    const allPromises = [];
    for (var i = 0; i < files.length; i++) {
      var data = new FormData();
      data.append('file', files[i] as unknown as Blob);
      const promise = restClient.post(`files/${moduleName}/${name}/upload`, data);
      allPromises.push(promise);
    }
    return await Promise.all(allPromises);
  };

  const uploadAcceptedFiles = async (files: File[]) => {
    const variant: VariantType = 'error';
    setLoading(true);
    setAttachedFiles(files);
    if (!multiple) {
      setFileList([]);
    }
    try {
      const imageObjects = await uploadSiteImages(files);
      const imageDetails = imageObjects.map((file) => {
        const { data } = file;
        return data;
      });
      const newFiles = multiple ? [...fileList, ...imageDetails] : imageDetails;
      setFileList(newFiles);
      setAttachedFiles([]);
      setLoading(false);
    } catch (error) {
      enqueueCustomSnackbar(t('uploadFailed'), {
        variant,
      });
    }
  };

  // Toaster to display warning message when file are invalid
  const rejectedFilesToaster = (rejectedFiles: any) => {
    const rejectedFile = rejectedFiles?.[0]?.errors[0];
    const variant: VariantType = 'warning';
    let warningMessage: string;
    switch (rejectedFile['code']) {
      case 'file-invalid-type':
        warningMessage = t('rejectedFileTypes');
        break;
      case 'file-too-large':
        warningMessage = `${t('rejectedFilesLarge', {
          size: bytesToMegaBytesSize(IMAGE_SIZE),
        })} ${t('mb')}`;
        break;
      default:
        warningMessage = rejectedFile['message'];
        break;
    }
    return rejectedFiles.length > 0
      ? enqueueCustomSnackbar(warningMessage, {
          variant,
        })
      : null;
  };

  const renderDeleteButtons = useCallback(() => {
    return (
      <>
        <Button onClick={() => setDeleteDialogOpen(false)}>{t('cancel')}</Button>
        <Button variant='contained' color='secondary' onClick={deleteImage} disabled={loading}>
          {t('delete')}
        </Button>
      </>
    );
  }, [deleteImage, loading, t]);

  const renderDeleteDialog = useCallback(() => {
    return (
      <Dialog open={deleteDialogOpen} title={t('deleteImage')} buttons={renderDeleteButtons()}>
        {t('deleteConfirm', { name: typeof file !== 'string' ? file?.name : t('unknown') })},
      </Dialog>
    );
  }, [deleteDialogOpen, file, renderDeleteButtons, t]);

  const renderImageUploadDialog = () => {
    return (
      <>
        <Dialog
          open={dialogOpen}
          title={t('attachFiles')}
          buttons={renderUploadActionButtons()}
          dialogProps={{
            fullWidth: true,
            maxWidth: 'xl',
          }}
        >
          {mode === 'mixed' && (
            <Tabs value={tabIndex} onChange={handleTabChange}>
              <Tab label={t('common:upload')} />
              <Tab label={t('common:library')} />
            </Tabs>
          )}

          <TabPanel index={0} value={tabIndex}>
            <div className={classes.contentWrapper}>
              <div className={classes.dropZone}>
                {dialogOpen && (
                  <FileDropZone
                    buttonProps={{
                      props: {
                        variant: 'contained',
                        color: 'secondary',
                        size: 'small',
                      },
                      defaultComponent: 'button',
                    }}
                    label={t('browse')}
                    acceptedFiles={uploadAcceptedFiles as any}
                    accept={FILE_EXTENSION}
                    fileSize={IMAGE_SIZE}
                    rejectedFiles={rejectedFilesToaster}
                    multiple={multiple}
                    dropZoneLabel={t('dropZoneTitle')}
                    btnTextStyle={classes.btnText}
                  />
                )}
                {!isEmpty(previewImage) && (
                  <Card className={classes.previewCard}>
                    <CardHeader title={t('preview')} className={classes.previewTitle} />
                    <ImageCardContent file={previewImage} dateFormat={DATE_FORMAT} />
                    <ImagePreview fileContent={previewImage} height={225} />
                  </Card>
                )}
              </div>
              <Divider orientation='vertical' flexItem className={classes.divider} />
              <div className={classes.listFiles}>
                {fileList?.length > 0 || attachedFiles?.length ? (
                  <ImageList
                    files={fileList}
                    setPreviewImage={setPreviewImage}
                    onDelete={deleteConfirmation}
                    loading={loading}
                    moduleName={moduleName}
                    fieldName={name}
                    attachedFiles={attachedFiles}
                  />
                ) : (
                  <div className={classes.emptyContent}>
                    <Alert severity='info' icon={<Info fontSize='small' />}>
                      {t('emptyUploadedMessage')}
                    </Alert>
                  </div>
                )}
              </div>
            </div>
          </TabPanel>
          <TabPanel index={1} value={tabIndex}>
            <ImageLibrarySelect
              sourceId={sourceId!}
              files={fileList}
              setValue={setFileList}
              multiple={multiple}
            />
          </TabPanel>
        </Dialog>
        {renderDeleteDialog()}
      </>
    );
  };
  return renderImageUploadDialog();
};

export default ImageUploadDialog;
